import {
  drybulkCargoOperationsService,
  type DelayRequest,
} from "@services/drybulkCargoOperationsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { DRYBULK_CARGO_OPS_QUERY_KEYS, DRYBULK_CARGO_OPS_TELEMETRY } from "../constants";

const context = (action: string, operationId: string) => ({
  module: DRYBULK_CARGO_OPS_TELEMETRY.module,
  screen: DRYBULK_CARGO_OPS_TELEMETRY.delaysScreen,
  action,
  entityType: "cargo_operation",
  entityId: operationId,
});

/**
 * DBCO-0059 (R-30) - add, update and remove a delay. The server decides every rule and its refusals are shown by the caller; on
 * success the delays and the Final Delay Report (which is built from them) are refetched. No payload value reaches telemetry.
 */
export const useDelayMutations = (operationId: string) => {
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.delays(operationId) }),
      queryClient.invalidateQueries({
        queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.finalDelayReport(operationId),
      }),
    ]);
  const add = useMutation({
    mutationFn: (request: DelayRequest) =>
      runTransaction(context("add_operation_delay", operationId), (transactionId) =>
        drybulkCargoOperationsService.addDelay(operationId, request, { transactionId }),
      ),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ delayId, request }: { delayId: string; request: DelayRequest }) =>
      runTransaction(context("update_operation_delay", operationId), (transactionId) =>
        drybulkCargoOperationsService.updateDelay(operationId, delayId, request, { transactionId }),
      ),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (delayId: string) =>
      runTransaction(context("delete_operation_delay", operationId), (transactionId) =>
        drybulkCargoOperationsService.deleteDelay(operationId, delayId, { transactionId }),
      ),
    onSuccess: refresh,
  });
  return { add, update, remove };
};
